import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  Spinner,
  StackDivider,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import {
  DialogBody,
  DialogContainer,
  DialogFooter,
  DialogHeader
} from '@components'
import { Upload } from '@components/icons'
import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  useClinicControllerArchiveDocumentReference,
  useClinicControllerEditDocumentReference,
  useClinicControllerGetDocumentReferences,
  useUploadControllerV1UploadFile
} from '~/clinician-api'
import { PolicyItem } from './policy-item'

const MAX_FILE_SIZE_BYTES = 1e7 // 10MB

interface IntakeDocsProps {
  selectedClinic: {
    id: string
  }
}

export const IntakeDocs: React.FC<IntakeDocsProps> = ({ selectedClinic }) => {
  const [documentReferenceToDelete, setDocumentReferenceToDelete] = useState<
    string | null
  >(null)
  const [editingDocumentReferenceId, setEditingDocumentReferenceId] = useState<
    string | null
  >(null)
  const toast = useToast()

  const { mutateAsync: uploadFile, isLoading: isUploading } =
    useUploadControllerV1UploadFile()
  const { data: documentReferences, refetch: refetchDocumentReferences } =
    useClinicControllerGetDocumentReferences(selectedClinic.id, {
      categoryCode: 'ClinicPrivacyPolicy'
    })

  const { mutateAsync: archiveDocumentReference } =
    useClinicControllerArchiveDocumentReference()
  const { mutateAsync: editDocumentReference } =
    useClinicControllerEditDocumentReference()

  const handleFileUpload = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return
      }

      for (const file of acceptedFiles) {
        await uploadFile({
          data: { file, categoryCode: 'ClinicPrivacyPolicy' }
        })
      }

      refetchDocumentReferences()
      toast({
        title: 'Document uploaded',
        status: 'success'
      })
    },
    [uploadFile, refetchDocumentReferences, toast]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileUpload,
    accept: { 'application/pdf': ['.pdf'] },
    maxSize: MAX_FILE_SIZE_BYTES,
    onDropRejected(fileRejections) {
      toast({
        status: 'error',
        title: fileRejections[0].errors[0].message
      })
    }
  })

  const handleDeleteDocumentReference = async (documentReferenceId: string) => {
    setDocumentReferenceToDelete(documentReferenceId)
  }

  const handleCloseDeleteDialog = () => {
    setDocumentReferenceToDelete(null)
  }

  const handleStartEditing = (documentReferenceId: string | null) => {
    setEditingDocumentReferenceId(documentReferenceId)
  }

  const handleSaveDocumentReferenceName = async (
    documentReferenceId: string,
    name: string
  ) => {
    await editDocumentReference({
      clinicId: selectedClinic.id,
      documentReferenceId,
      data: { description: name }
    })
    refetchDocumentReferences()
  }

  const handleConfirmDelete = async () => {
    if (!documentReferenceToDelete) {
      return
    }

    await archiveDocumentReference({
      clinicId: selectedClinic.id,
      documentReferenceId: documentReferenceToDelete
    })
    refetchDocumentReferences()
    toast({
      status: 'success',
      title: 'Document removed from intake package'
    })
    handleCloseDeleteDialog()
  }

  return (
    <>
      <Box pb="xlarge">
        <Box mb={'medium'} w={'100%'}>
          <Heading mb="xxsmall">Intake Documents</Heading>
          <Text>
            Blueprint will auto-send your consent and policy forms for you.
            Upload and manage your practice's forms here
          </Text>
        </Box>
        <Box
          border="1px solid"
          borderColor="gray.300"
          borderRadius="md"
          overflow="hidden"
        >
          <Box p="xsmall">
            <Text fontWeight="bold" fontSize="sm" color="#757575">
              POLICY FORMS
            </Text>
          </Box>
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="265px"
            {...getRootProps()}
            borderY="1px solid"
            borderColor="gray.300"
            p="4"
          >
            <Image
              width="177px"
              height="133px"
              src="https://res.cloudinary.com/hellojoy/image/upload/v1750875020/images/intake.png"
              alt="intake-upload"
            />

            <Button borderRadius="8px" leftIcon={<Upload fill="white" />}>
              Upload your policy pdfs
            </Button>
            <Text
              mt="xsmall"
              fontSize="12px"
              textAlign="center"
              maxWidth="340px"
            >
              Upload PDFs for privacy, consent, and any state or
              practice-specific policies—everything clients need to get started.
            </Text>

            <input {...getInputProps()} />
          </Flex>
          <VStack
            spacing="0px"
            align="flex-start"
            divider={
              <StackDivider
                borderBottom="1px solid"
                borderBottomColor="pale_gray"
              />
            }
          >
            {isUploading && (
              <Flex w="100%" p="12px" alignItems="center" gap="small">
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  p="12px"
                  bg="rgba(87, 89, 91, 0.04)"
                  borderRadius="8px"
                >
                  <Spinner color="primary" />
                </Flex>
                <Text fontWeight="bold">Uploading document...</Text>
              </Flex>
            )}
            {documentReferences?.map(documentReference => (
              <PolicyItem
                key={documentReference.id}
                isEditing={editingDocumentReferenceId === documentReference.id}
                documentReference={documentReference}
                onSaveDocumentReferenceName={handleSaveDocumentReferenceName}
                onStartEditing={handleStartEditing}
                onDeleteDocumentReference={handleDeleteDocumentReference}
              />
            ))}
          </VStack>
        </Box>
      </Box>
      <DialogContainer
        isOpen={!!documentReferenceToDelete}
        onClose={handleCloseDeleteDialog}
      >
        <DialogHeader text="Are you sure?" onClose={handleCloseDeleteDialog} />
        <DialogBody>
          <Text>
            This will remove the document from future intake packages.{' '}
          </Text>
          <Text mt="small">
            It'll stay in the records for any clients who already approved it.
          </Text>
          <HStack spacing={'medium'} w="100%" mt="large">
            <Button
              onClick={handleCloseDeleteDialog}
              variant="outline"
              size="lg"
              borderRadius="8px"
            >
              Cancel
            </Button>
            <Button
              flexShrink={0}
              flexGrow={1}
              bg="severe"
              borderRadius="8px"
              size="lg"
              onClick={handleConfirmDelete}
            >
              Confirm
            </Button>
          </HStack>
        </DialogBody>
      </DialogContainer>
    </>
  )
}
